import { useTranslation } from 'react-i18next'

import { useCandidates } from '../candidatesContext/useCandidatesContext'
import { useOrganisationSearch } from '../../hooks/useOrganisationSearch'
import { CandidatesReport } from '../candidatesReport/CandidatesReport'
import { AddOrganisation } from '../addOrganisation/AddOrganisation'
import { useTaskAssignees } from '../../hooks/useTaskAssignees'

import { FilterList, FilterRange, Flex, SearchInput, TableSearchAndFilters } from '@percent/lemonade'
import { SearchAndFilterProps } from './SearchAndFilters.types'
import { POSTHOG_FEATURE_FLAGS, usePostHogFeatureFlag } from '@percent/partner-dashboard/common/hooks'
import { useAssigneeFilter } from './hooks/useAssigneeFilter'
import { useStateFilter } from './hooks/useStateFilter'
import { useScoreFilter } from './hooks/useScoreFilter'

export function SearchAndFilters({
  enabledFilters = { organisationSearch: true, currentState: true },
  validStates,
  scoreName
}: Readonly<SearchAndFilterProps>) {
  const { queryParams, setQueryParams, candidates } = useCandidates()
  const { t } = useTranslation()
  const grantsCsvExportFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.PD_GRANTS_CSV_EXPORT)
  const { taskAssignees, loadingTaskAssignees } = useTaskAssignees()

  const isSearchActive = !!queryParams.searchTerm && queryParams.searchTerm.trim() !== ''
  const urlAssigneeEmails = (queryParams.assigneeEmails ?? []).filter((v): v is string => !!v)
  const includeUnassigned = Boolean(queryParams.includeUnassigned)
  const assigneeFilterApplied = urlAssigneeEmails.length > 0 || includeUnassigned
  const { organisation, organisationSearch } = useOrganisationSearch({ queryParams, setQueryParams })

  const {
    assigneeSelection,
    assigneeOptions,
    assigneeFilterReady: assigneeReady,
    handleAssigneeFilterChange
  } = useAssigneeFilter({ taskAssignees, loadingTaskAssignees, urlAssigneeEmails, includeUnassigned, setQueryParams })

  const { filterOptions, selectedFilterValues, handleFilterChange } = useStateFilter({
    validStates,
    queryParams,
    setQueryParams
  })

  const { scoreRange, scoreFilterApplied, handleScoreRangeChange } = useScoreFilter({
    scoreName,
    queryParams,
    setQueryParams
  })

  const filtersApplied =
    (!!enabledFilters.currentState && (queryParams.currentState?.length ?? 0) > 0) ||
    isSearchActive ||
    assigneeFilterApplied ||
    scoreFilterApplied

  const displayInAllTab = queryParams.activeTab.toLowerCase() === 'all'
  const displayCSVExportBtn = candidates.totalResults > 0 && grantsCsvExportFeatureFlag

  return (
    <TableSearchAndFilters>
      <Flex justify="space-between" align="center" w="100%">
        {(candidates.totalResults > 0 || filtersApplied) && (
          <Flex w="100%" maw="480" mr={8}>
            {enabledFilters.organisationSearch && (
              <SearchInput
                name="organisation"
                value={organisation}
                handleClearValue={organisationSearch.handleClearOrganisationValue}
                onKeyDown={organisationSearch.handleKeyPress}
                onChange={organisationSearch.onOrganisationChange}
                onBlur={organisationSearch.validateOrganisation}
                placeholder={t('table.candidatesSearchPlaceholder')}
                status="default"
                inputSize="small"
              />
            )}
            <Flex align="center" ml={8}>
              {enabledFilters.currentState && (
                <FilterList
                  label={t('grants.candidateState')}
                  options={filterOptions}
                  selectedValues={selectedFilterValues}
                  onChange={handleFilterChange}
                  testId="candidate-state-filter"
                />
              )}
              {enabledFilters.score && scoreName && (
                <FilterRange
                  label={scoreName}
                  value={scoreRange}
                  onChange={handleScoreRangeChange}
                  minFieldLabel={t('grants.filters.score.min')}
                  maxFieldLabel={t('grants.filters.score.max')}
                  minFieldTooltip={t('grants.filters.score.minTooltip')}
                  maxFieldTooltip={t('grants.filters.score.maxTooltip')}
                  applyButtonText={t('button.apply')}
                  clearButtonText={t('button.clear')}
                  testId="score-range-filter"
                />
              )}
              {enabledFilters.assignee && assigneeReady && (
                <div data-testid="assignee-filter">
                  <FilterList
                    label={t('grants.filters.assignee.label')}
                    options={assigneeOptions}
                    selectedValues={assigneeSelection}
                    onChange={handleAssigneeFilterChange}
                  />
                </div>
              )}
            </Flex>
          </Flex>
        )}
        {displayInAllTab && (
          <Flex align="center" gap={12} ml="auto">
            {displayCSVExportBtn && <CandidatesReport />}
            <AddOrganisation />
          </Flex>
        )}
      </Flex>
    </TableSearchAndFilters>
  )
}
